import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { RollerApiService } from 'src/app/_services/soap/roller-api.service';
import {
  buildSoapExecutePayload,
  formatXml,
  getSoapExecuteErrorMessage,
  prepareSoapOperationRequest,
  PreparedSoapOperationRequest,
  ROLLER_SOAP_ENDPOINT,
  SoapExecuteResult,
} from 'src/app/_services/soap/roller-api-operations';
import { buildResponseTablesFromSoapResult } from 'src/app/_services/soap/roller-api-response-table';
import { DispatchCsvStation, DispatchPlannerCsvService } from './dispatch-planner-csv.service';

export class DispatchPlannerSoapError extends Error {
  constructor(
    message: string,
    readonly envelope: string,
    readonly responseBody = ''
  ) {
    super(message);
    this.name = 'DispatchPlannerSoapError';
  }
}

export interface DispatchPlannerSoapImportResult {
  stations: DispatchCsvStation[];
  envelope: string;
  responseBody: string;
}

function formatSoapRaw(raw?: string | null): string {
  if (!raw?.trim()) {
    return '';
  }

  const formatted = formatXml(raw);
  return formatted || raw;
}

@Injectable({
  providedIn: 'root'
})
export class DispatchPlannerRollerSoapService {
  constructor(
    private rollerApiService: RollerApiService,
    private csvService: DispatchPlannerCsvService
  ) {}

  fetchTouren(branchNo: string, calendarDay: string): Observable<DispatchPlannerSoapImportResult> {
    const prepared = prepareSoapOperationRequest('getTouren', {
      branchNo: branchNo.trim(),
      calendarDay: calendarDay.trim(),
      kvNo: '',
    });

    if (!prepared) {
      return throwError(() => new DispatchPlannerSoapError(
        'SOAP-Operation getTouren nicht konfiguriert.',
        ''
      ));
    }

    const requestEnvelope = formatXml(prepared.envelope);

    if (prepared.validationError) {
      return throwError(() => new DispatchPlannerSoapError(prepared.validationError, requestEnvelope));
    }

    const payload = buildSoapExecutePayload(prepared.operation, prepared.envelope, ROLLER_SOAP_ENDPOINT);

    return this.rollerApiService.execute(payload).pipe(
      map((result) => this.processExecuteResult(result, prepared)),
      catchError((err: HttpErrorResponse | DispatchPlannerSoapError | Error) => {
        if (err instanceof DispatchPlannerSoapError) {
          return throwError(() => err);
        }

        const httpError = err as HttpErrorResponse;
        if (httpError?.error && typeof httpError.error === 'object') {
          try {
            return of(this.processExecuteResult(httpError.error as SoapExecuteResult, prepared));
          } catch (processErr) {
            if (processErr instanceof DispatchPlannerSoapError) {
              return throwError(() => processErr);
            }
          }
        }

        if (err instanceof Error) {
          return throwError(() => new DispatchPlannerSoapError(err.message, requestEnvelope));
        }

        const body = httpError.error as Record<string, unknown> | null;
        const request = body?.request;
        const requestEnvelopeFromBody =
          request &&
          typeof request === 'object' &&
          typeof (request as Record<string, unknown>).envelope === 'string'
            ? (request as Record<string, unknown>).envelope as string
            : prepared.envelope;
        const fallbackMessage = `SOAP-Anfrage fehlgeschlagen (${httpError.status || 'Netzwerk'}).`;
        const message =
          (typeof body?.error === 'string' && body.error) ||
          (typeof body?.message === 'string' && body.message) ||
          fallbackMessage;
        const envelope = formatXml(requestEnvelopeFromBody);
        const responseBody = formatSoapRaw(typeof body?.raw === 'string' ? body.raw : '');

        return throwError(() => new DispatchPlannerSoapError(message, envelope, responseBody));
      })
    );
  }

  private processExecuteResult(
    result: SoapExecuteResult,
    prepared: PreparedSoapOperationRequest
  ): DispatchPlannerSoapImportResult {
    const envelope = formatXml(result.request?.envelope || prepared.envelope);
    const responseBody = formatSoapRaw(result.raw);

    const executeError = getSoapExecuteErrorMessage(result);
    if (executeError) {
      throw new DispatchPlannerSoapError(executeError, envelope, responseBody);
    }

    const tables = buildResponseTablesFromSoapResult(result.parsed, result.raw);
    const auftraegeTable = tables.find((table) => table.title === 'auftraege');
    if (!auftraegeTable?.rows.length) {
      throw new DispatchPlannerSoapError(
        'Keine Aufträge in der SOAP-Antwort gefunden.',
        envelope,
        responseBody
      );
    }

    const stations = this.csvService.groupSoapTourRows(auftraegeTable.rows);
    if (!stations.length) {
      throw new DispatchPlannerSoapError(
        'Keine gültigen Kaufvertragsnummern in der SOAP-Antwort.',
        envelope,
        responseBody
      );
    }

    return { stations, envelope, responseBody };
  }
}
